/**
 * Graph Color Mappings
 *
 * Color schemes for visualizing organizations by sector and region. Sector
 * names are stable enough across tenants that a named map is still useful as
 * a default, but **region** names are tenant-specific (`ecosystem.taxonomy.
 * regions`). For regions we build a deterministic hash-based palette so the
 * same region name always gets the same color regardless of tenant: without
 * hardcoding a Colorado-shaped list.
 */

// Sector colors: primary organization classification. Stable cross-tenant
// names (the schema's `organizationSectors`). Unknown sectors fall through
// to DEFAULT_COLOR.
export const SECTOR_COLORS = {
    'Non-profit': '#42A5F5',
    'PreK-12 education': '#FF7043',
    'Post-secondary/Higher education': '#66BB6A',
    'Business/Industry': '#5C6BC0',
    'Government/Policy maker': '#EF5350',
    'Coalition/Alliance': '#26A69A',
    'Foundation/Funder': '#AB47BC',
    Agriculture: '#8D6E63',
    'Individual/Community': '#78909C',
    'Medical/Health care': '#EC407A',
    Military: '#7E57C2',
    'National lab': '#29B6F6',
    'Non formal education': '#FFCA28',
    'Professional organization': '#9CCC65',
    'Sports organization': '#FFA726',
};

// Default palette used when building a region color map dynamically. Picked
// for high contrast against each other and against typical white backgrounds.
// 12 colors is plenty for any single tenant's region count without repeating.
const REGION_PALETTE = [
    '#4CAF50',
    '#E91E63',
    '#03A9F4',
    '#9C27B0',
    '#FF9800',
    '#795548',
    '#607D8B',
    '#00BCD4',
    '#F44336',
    '#3F51B5',
    '#8BC34A',
    '#FFC107',
];

// Default color for unknown sectors/regions
export const DEFAULT_COLOR = '#BDBDBD';

/**
 * Deterministic string hash (djb2). Pure, stable across runs / browsers.
 *
 * @param {string} str
 * @returns {number}
 */
const hashString = (str) => {
    let hash = 5381;
    for (let i = 0; i < str.length; i += 1) {
        hash = (hash * 33) ^ str.charCodeAt(i);
    }
    // Convert to unsigned 32-bit so the modulus index is always non-negative.
    return hash >>> 0;
};

/**
 * Build a stable label → color map for an arbitrary list of region names.
 * Same input list ⇒ same output map, regardless of insertion order. The
 * "Not currently serving X" buckets render in grey, matching the original
 * neutral treatment for the platform-mode Colorado list.
 *
 * @param {string[]} regions - Region names from `ecosystem.taxonomy.regions`.
 * @returns {Record<string, string>} Region name → hex color.
 */
export const buildRegionColors = (regions) => {
    if (!Array.isArray(regions) || regions.length === 0) {
        return {};
    }
    const map = {};
    regions.forEach((region) => {
        if (!region || typeof region !== 'string') {
            return;
        }
        // Treat "not currently serving …" as a neutral bucket regardless of
        // the actual phrasing: matches the Colorado-era convention.
        if (/^not currently serving/i.test(region)) {
            map[region] = '#9E9E9E';
            return;
        }
        const idx = hashString(region) % REGION_PALETTE.length;
        map[region] = REGION_PALETTE[idx];
    });
    return map;
};

/**
 * Get color for a node based on coloring mode.
 *
 * @param {object} node - Node object with primarySector and regionsServed.
 * @param {string} colorBy - 'sector' or 'region'.
 * @param {Record<string, string>} [regionColors] - Tenant-specific region
 *   color map (from `buildRegionColors`). Regions default to
 *   `DEFAULT_COLOR` when not provided.
 * @returns {string} Hex color code.
 */
export function getNodeColor(node, colorBy = 'sector', regionColors = {}) {
    if (colorBy === 'region') {
        const region =
            node.regionsServed && node.regionsServed.length > 0
                ? node.regionsServed[0]
                : null;
        if (!region) {
            return DEFAULT_COLOR;
        }
        // Default parameters only apply to `undefined`; a caller passing an
        // explicit `null` reaches here otherwise, so guard again.
        return (regionColors || {})[region] || DEFAULT_COLOR;
    }

    // Default to sector coloring
    return SECTOR_COLORS[node.primarySector] || DEFAULT_COLOR;
}

/**
 * Get legend items for the current color mode.
 *
 * @param {string} colorBy - 'sector' or 'region'.
 * @param {Record<string, string>} [regionColors] - Tenant-specific region
 *   color map (from `buildRegionColors`). Empty legend when not provided.
 * @returns {Array<{label: string, color: string}>}
 */
export function getLegendItems(colorBy = 'sector', regionColors = {}) {
    if (colorBy === 'region') {
        // Default parameters only apply to `undefined`; guard against an
        // explicit `null` too.
        return Object.entries(regionColors || {}).map(([label, color]) => ({
            label,
            color,
        }));
    }
    return Object.entries(SECTOR_COLORS).map(([label, color]) => ({
        label,
        color,
    }));
}
